import { isNil } from 'ramda';
import { useAppSelector } from '@/store/hooks';
import { areaIdByNameAndCollectionIdSelector, areasItemsSelector } from './areas';
import { selectAreaName } from '@/store/slices/sessionSlice';
import { useActiveUser } from '@/platform-services/auth';
import { useSessionModelType } from '@/platform-services/session/useSessionModelType';
import { EDITING_ALLOWED_PERMISSION_ROLES, Role, SearchForRetail, SearchForRetailRole, SEO_EDITING_ALLOWED_PERMISSION_ROLES, } from '@/constants';
import { useActiveModelPath } from '@/routing/hooks/utils';
import { collectionIdByName } from '@/store/selectors';
import { getRouteIdentifierFromPath } from '@/routing';
export const useAllAccessibleAreas = () => {
    const activeUser = useActiveUser();
    const allAreas = useAppSelector((state) => state.areas.items);
    if (isNil(activeUser) || isNil(allAreas)) {
        return [];
    }
    return allAreas.filter(({ id }) => { var _a; return (_a = activeUser.areaPermissions) === null || _a === void 0 ? void 0 : _a.some((areaPermission) => areaPermission.areaId === id); });
};
export const useCanViewUsers = () => {
    const activeUser = useActiveUser();
    if (isNil(activeUser)) {
        return false;
    }
    return activeUser.allowedAccess.includes(Role.ADMIN);
};
export const areaIdByName = (collectionName, areaName) => (state) => {
    const collectionId = collectionIdByName(collectionName)(state);
    return areaIdByNameAndCollectionIdSelector(collectionId, areaName)(state);
};
/**
 * Returns the area ID from the current session state.
 *
 * IMPORTANT: This can return `undefined` when:
 * - The session is not fully initialized (collectionName or areaName missing)
 * - The specified collection or area doesn't exist in state
 * - The app is still loading initial data
 *
 * Callers must handle the undefined case appropriately.
 */
export const areaIdSelector = (state) => areaIdByName(state.session.collectionName, state.session.areaName)(state);
export const collectionIdSelector = (state) => collectionIdByName(state.session.collectionName)(state);
export const useAllowedAreasSelector = () => {
    var _a, _b;
    const activeUser = useActiveUser();
    return (_b = (_a = activeUser === null || activeUser === void 0 ? void 0 : activeUser.areaPermissions) === null || _a === void 0 ? void 0 : _a.map(({ areaId }) => String(areaId))) !== null && _b !== void 0 ? _b : [];
};
export const useIsEditingAllowed = () => {
    var _a, _b, _c;
    const activeUser = useActiveUser();
    const activeUserRole = activeUser === null || activeUser === void 0 ? void 0 : activeUser.role;
    const isAdminUser = activeUserRole === SearchForRetailRole.Admin;
    const activeModelType = useSessionModelType();
    const shouldBeAreaMerchandiser = [
        SearchForRetail.PageType.AREA,
        SearchForRetail.PageType.SITE,
    ].includes(SearchForRetail.PAGE_TYPE_MAP[activeModelType]);
    const area = useAppSelector(selectAreaName);
    const collectionId = useAppSelector(collectionIdSelector);
    const areaId = useAppSelector(areaIdByNameAndCollectionIdSelector(collectionId, area));
    const areaPermission = (_c = (_b = (_a = activeUser === null || activeUser === void 0 ? void 0 : activeUser.areaPermissions) === null || _a === void 0 ? void 0 : _a.find((p) => String(p.areaId) === String(areaId))) === null || _b === void 0 ? void 0 : _b.role) === null || _c === void 0 ? void 0 : _c.name;
    const isAreaMerchandiserUser = EDITING_ALLOWED_PERMISSION_ROLES.includes(areaPermission);
    return isAdminUser || (shouldBeAreaMerchandiser && isAreaMerchandiserUser);
};
export const useIsSeoEditingAllowed = () => {
    var _a, _b, _c;
    const activeUser = useActiveUser();
    const activeUserRole = activeUser === null || activeUser === void 0 ? void 0 : activeUser.role;
    const isAdminUser = activeUserRole === SearchForRetailRole.Admin;
    const activeModelType = useSessionModelType();
    const isPageAreaOrSiteDependent = [
        SearchForRetail.PageType.AREA,
        SearchForRetail.PageType.SITE,
    ].includes(SearchForRetail.PAGE_TYPE_MAP[activeModelType]);
    const area = useAppSelector(selectAreaName);
    const collectionId = useAppSelector(collectionIdSelector);
    const areaId = useAppSelector(areaIdByNameAndCollectionIdSelector(collectionId, area));
    const areaPermission = (_c = (_b = (_a = activeUser === null || activeUser === void 0 ? void 0 : activeUser.areaPermissions) === null || _a === void 0 ? void 0 : _a.find((p) => String(p.areaId) === String(areaId))) === null || _b === void 0 ? void 0 : _b.role) === null || _c === void 0 ? void 0 : _c.name;
    const isSeoUser = SEO_EDITING_ALLOWED_PERMISSION_ROLES.includes(areaPermission);
    return isAdminUser || (isPageAreaOrSiteDependent && isSeoUser);
};
export function filterAreasToPromote(areas, areaPermissions, currentAreaName) {
    const userAllowedAreas = new Set(areaPermissions === null || areaPermissions === void 0 ? void 0 : areaPermissions.map(({ areaId }) => areaId));
    return areas.reduce((acc, { id, name, collectionId }) => {
        if (!userAllowedAreas.has(id) || currentAreaName === name) {
            return acc;
        }
        return [...acc, { label: name, id: String(id), collectionId: String(collectionId) }];
    }, []);
}
export const useAllowedAreasToPromote = () => {
    const activeUser = useActiveUser();
    const currentAreaName = useAppSelector(selectAreaName);
    const areas = useAppSelector(areasItemsSelector);
    return filterAreasToPromote(areas, activeUser === null || activeUser === void 0 ? void 0 : activeUser.areaPermissions, currentAreaName);
};
export const useAreaIdSelector = () => useAppSelector(areaIdSelector);
export const useCollectionIdSelector = () => useAppSelector(collectionIdSelector);
export const useTenantIdSelector = () => {
    const activeUser = useActiveUser();
    return activeUser === null || activeUser === void 0 ? void 0 : activeUser.tenantId;
};
export const useGetConfigPageType = () => {
    const modelPath = useActiveModelPath();
    const modelType = getRouteIdentifierFromPath(modelPath);
    return SearchForRetail.PAGE_TYPE_MAP[modelType];
};
